import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import type { Candidate } from "../types";
import { useRejectedCandidateForm } from "./hooks/useRejectedCandidateForm";
import { REJECTION_REASONS, type RejectionReason } from "./constants/rejectionReasons";

interface RejectedCandidateFormProps {
  candidate: Candidate;
  open: boolean;
  onClose: () => void;
  onSave: (candidate: Candidate) => void;
}

export const RejectedCandidateForm = ({ 
  candidate, 
  open, 
  onClose, 
  onSave 
}: RejectedCandidateFormProps) => {
  const {
    selectedReasons,
    otherReason,
    setOtherReason,
    rejectionNotes,
    setRejectionNotes,
    rejectedDate,
    setRejectedDate,
    handleReasonToggle,
    handleSubmit,
    isSubmitting,
  } = useRejectedCandidateForm({ candidate, onSave, onClose });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Reject Candidate - {candidate.first_name} {candidate.last_name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Rejection Reasons */}
          <div className="space-y-3">
            <Label className="text-base font-medium">
              Rejection Reasons *
            </Label>
            <p className="text-sm text-muted-foreground">
              Select all applicable reasons (multiple selections allowed)
            </p>
            <div className="grid grid-cols-1 gap-3 max-h-48 overflow-y-auto border rounded-md p-3">
              {REJECTION_REASONS.map((reason) => (
                <div key={reason} className="flex items-center space-x-2">
                  <Checkbox
                    id={reason}
                    checked={selectedReasons.includes(reason)}
                    onCheckedChange={() => handleReasonToggle(reason)}
                  />
                  <Label
                    htmlFor={reason}
                    className="text-sm font-normal cursor-pointer flex-1"
                  >
                    {reason}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Other Reason Input */}
          {selectedReasons.includes("Other") && (
            <div className="space-y-2">
              <Label htmlFor="otherReason" className="text-sm font-medium">
                Please specify other reason *
              </Label>
              <Input
                id="otherReason"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Enter custom rejection reason..."
                className="w-full"
              />
            </div>
          )}

          {/* Rejection Date */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Rejection Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !rejectedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {rejectedDate ? format(rejectedDate, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={rejectedDate}
                  onSelect={(date) => date && setRejectedDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Additional Notes */}
          <div className="space-y-2">
            <Label htmlFor="rejectionNotes" className="text-sm font-medium">
              Additional Notes (Optional)
            </Label>
            <Textarea
              id="rejectionNotes"
              value={rejectionNotes}
              onChange={(e) => setRejectionNotes(e.target.value)}
              placeholder="Add any additional context about the rejection..."
              rows={3}
              className="w-full"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isSubmitting ? "Rejecting..." : "Reject Candidate"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
